/**
 * Copyright (c) 2025 Aisle 24. All rights reserved.
 *
 * Proprietary Software - Licensed for Internal Use Only
 *
 * Update Checker Service
 * Checks for new versions and fetches release notes
 */

import { APP_CONFIG } from '../constants/appConstants';

export interface VersionInfo {
  version: string;
  buildNumber: number;
  lastUpdated: string;
  downloadUrl: string;
  releaseNotes: string;
  minRequiredVersion?: string;
  forceUpdate?: boolean;
}

export interface UpdateCheckResult {
  hasUpdate: boolean;
  currentVersion: string;
  currentBuild: number;
  latestVersion: string;
  latestBuild: number;
  releaseNotes: string;
  downloadUrl: string;
  forceUpdate: boolean;
}

class UpdateChecker {
  private versionApiUrl: string;
  private checkInterval: number = 5 * 60 * 1000; // Check every 5 minutes
  private intervalId: NodeJS.Timeout | null = null;
  private onUpdateCallback: ((result: UpdateCheckResult) => void) | null = null;
  private isDevelopment: boolean;

  constructor() {
    // Get version API URL from environment or use default
    this.versionApiUrl = process.env.REACT_APP_VERSION_API_URL || 
      'http://143.244.128.192:8082/api/version.json';
    
    // Detect if running in development mode (localhost)
    // In development, we don't check for updates or increment build numbers
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    this.isDevelopment = 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      process.env.NODE_ENV === 'development';
    
    if (this.isDevelopment) {
      console.log('UpdateChecker: Running in development mode - update checks disabled');
    }
  }

  /**
   * Check for updates
   * Only works in production/kiosk mode - disabled in development
   */
  async checkForUpdates(): Promise<UpdateCheckResult> {
    // Skip update checks in development mode
    if (this.isDevelopment) {
      return {
        hasUpdate: false,
        currentVersion: APP_CONFIG.VERSION,
        currentBuild: APP_CONFIG.BUILD_NUMBER,
        latestVersion: APP_CONFIG.VERSION,
        latestBuild: APP_CONFIG.BUILD_NUMBER,
        releaseNotes: '',
        downloadUrl: '',
        forceUpdate: false,
      };
    }
    
    try {
      const response = await fetch(this.versionApiUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const versionInfo: VersionInfo = await response.json();

      const currentBuild = APP_CONFIG.BUILD_NUMBER;
      const latestBuild = versionInfo.buildNumber;

      const hasUpdate = latestBuild > currentBuild;

      return {
        hasUpdate,
        currentVersion: APP_CONFIG.VERSION,
        currentBuild,
        latestVersion: versionInfo.version,
        latestBuild,
        releaseNotes: versionInfo.releaseNotes || 'No release notes available',
        downloadUrl: versionInfo.downloadUrl,
        forceUpdate: versionInfo.forceUpdate || false,
      };
    } catch (error) {
      console.error('Error checking for updates:', error);
      return {
        hasUpdate: false,
        currentVersion: APP_CONFIG.VERSION,
        currentBuild: APP_CONFIG.BUILD_NUMBER,
        latestVersion: APP_CONFIG.VERSION,
        latestBuild: APP_CONFIG.BUILD_NUMBER,
        releaseNotes: '',
        downloadUrl: '',
        forceUpdate: false,
      };
    }
  }

  /**
   * Start automatic update checking
   * Only works in production/kiosk mode - disabled in development
   */
  startAutoCheck(onUpdate: (result: UpdateCheckResult) => void) {
    // Skip in development mode
    if (this.isDevelopment) {
      console.log('UpdateChecker: Auto-check disabled in development mode');
      return;
    }
    
    this.onUpdateCallback = onUpdate;
    
    // Check immediately
    this.checkForUpdates().then((result) => {
      if (result.hasUpdate) {
        this.onUpdateCallback?.(result);
      }
    });

    // Then check periodically
    this.intervalId = setInterval(async () => {
      const result = await this.checkForUpdates();
      if (result.hasUpdate) {
        this.onUpdateCallback?.(result);
      }
    }, this.checkInterval);
  }

  /**
   * Stop automatic update checking
   */
  stopAutoCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.onUpdateCallback = null;
  }

  /**
   * Trigger update (for Windows kiosk)
   * Executes auto-update.bat script to download and install the update
   */
  triggerUpdate() {
    if (window.navigator.platform.includes('Win')) {
      try {
        // Execute auto-update.bat via PowerShell
        // This works in Edge browser on Windows kiosks
        const projectDir = 'C:\\Aisle24POS';
        const updateScript = `${projectDir}\\auto-update.bat`;
        
        // Use PowerShell to execute the batch file
        const powershellCommand = `powershell.exe -Command "Start-Process -FilePath '${updateScript}' -WorkingDirectory '${projectDir}' -Verb RunAs -WindowStyle Hidden"`;
        
        // Try to execute via ActiveXObject (works in Edge/IE on Windows)
        try {
          // @ts-ignore - ActiveXObject is available in IE/Edge on Windows
          const shell = new ActiveXObject('WScript.Shell');
          shell.Run(powershellCommand, 0, false);
          console.log('Update script triggered successfully');
        } catch (activeXError) {
          // Fallback: Try using PowerShell via fetch to a local endpoint
          // Or show instructions
          console.warn('ActiveXObject not available, trying alternative method');
          
          // Alternative: Create a hidden iframe with a data URL that downloads a script
          // This is a workaround for browser security restrictions
          const instruction = `Please run the update script manually:\n\n1. Open File Explorer\n2. Navigate to: ${projectDir}\n3. Right-click "auto-update.bat"\n4. Select "Run as Administrator"`;
          
          // For now, show alert and provide instructions
          // In production, you might want to use a local helper service
          alert('Update will start automatically. If it doesn\'t, please run:\n\n' + updateScript + '\n\nas Administrator.');
          
          // Try to open the script location in File Explorer
          try {
            // @ts-ignore
            const shell = new ActiveXObject('Shell.Application');
            shell.ShellExecute('explorer.exe', projectDir, '', 'open', 1);
          } catch (e) {
            console.error('Could not open File Explorer:', e);
          }
        }
      } catch (error) {
        console.error('Error triggering update:', error);
        // Fallback: Show instructions
        alert('Please run C:\\Aisle24POS\\auto-update.bat as Administrator to update the application.');
      }
    } else {
      // For development on non-Windows, just reload
      console.log('Update triggered (development mode)');
      window.location.reload();
    }
  }
}

export const updateChecker = new UpdateChecker();

